/**
 * Emit hosts for the `team` analytics group — 15 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { isOnPrem } from '@/utils/buildProfile';
import { SegmentActionName, SegmentObjectName } from '@heidi/analytics/events';

export function trackAcceptTeamInvitation(input: {
  teamId: string;
  invitedEmail: string;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Team,
    props: {
      action: SegmentActionName.AcceptTeamInvitation,
      teamId: input.teamId,
      ...(isOnPrem ? {} : { invitedEmail: input.invitedEmail }),
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TeamMemberAccepted,
    props: {
      team_id: input.teamId,
      acceptance_action: 'accept_invitation',
      // PII redaction (OBS-1.C4): invited_email stripped on on-prem —
      // the in-cluster collector must never receive raw email addresses.
      // Cloud retains existing behavior.
      ...(isOnPrem ? {} : { invited_email: input.invitedEmail }),
    },
  });
}

export function trackDeleteTeam(input: { teamId: string }) {
  trackLegacyEvent({
    name: SegmentObjectName.Team,
    props: {
      action: SegmentActionName.DeleteTeam,
      teamId: input.teamId,
    },
  });
}

export function trackDeleteTeamMember(input: {
  teamId: string;
  memberId: string;
  memberEmail: string;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Team,
    props: {
      action: SegmentActionName.RemoveTeamMember,
      teamId: input.teamId,
      memberId: input.memberId,
      ...(isOnPrem ? {} : { memberEmail: input.memberEmail }),
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TeamMemberDeleted,
    props: {
      team_id: input.teamId,
      member_id: input.memberId,
      // PII redaction (OBS-1.C4): member_email stripped on on-prem —
      // field name doesn't match collector blacklist "email" (exact match),
      // so the collector will NOT dead-letter it. Must strip at source.
      ...(isOnPrem ? {} : { member_email: input.memberEmail }),
    },
  });
}

export function trackResendTeamInvitation(input: { teamId: string; email: string }) {
  trackLegacyEvent({
    name: SegmentObjectName.Team,
    props: {
      action: SegmentActionName.ResendTeamInvitation,
      teamId: input.teamId,
      ...(isOnPrem ? {} : { invitedEmail: input.email }),
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TeamMemberCreated,
    props: {
      team_id: input.teamId,
      // PII redaction (OBS-1.C4): email stripped on on-prem — defense-in-depth.
      ...(isOnPrem ? {} : { email: input.email }),
      creation_action: 'resend_invitation',
    },
  });
}

export function trackSelfOnboardingClicked() {
  trackLegacyEvent({
    name: SegmentObjectName.Team,
    props: {
      action: SegmentActionName.SelfOnboardingSubmitClicked,
    },
  });
}

export function trackTeamBillingClicked(teamId: string) {
  trackLegacyEvent({
    name: SegmentObjectName.Team,
    props: {
      action: SegmentActionName.TeamBillingClicked,
      teamId: teamId,
    },
  });

  // V2
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      label: 'team_billing_clicked',
      feature: 'team',
    },
  });
}

// trackTeamEvidenceAccessUpdated, trackTeamHeidiIiAccessUpdated moved to @heidi/workspace/data — history: git log --follow src/lib/analytics/scribe/team.ts

export function trackTeamUpdated(input: { teamId: string; teamName: string }) {
  trackLegacyEvent({
    name: SegmentObjectName.Team,
    props: {
      action: SegmentActionName.TeamUpdated,
      teamId: input.teamId,
      teamName: input.teamName,
    },
  });

  // V2
  track({
    event_name: TrackingEventName.TeamUpdated,
    props: {
      team_id: input.teamId,
      update_action: 'team_updated',
      team_name: input.teamName,
    },
  });
}

export function trackToggleTeamConsent(input: {
  teamId: string;
  enabled: boolean;
  adminId?: string;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Team,
    props: {
      action: SegmentActionName.TeamConsentToggle,
      teamId: input.teamId,
      adminId: input.adminId,
      enabled: input.enabled,
    },
  });

  // V2
  track({
    event_name: TrackingEventName.TeamUpdated,
    props: {
      team_id: input.teamId,
      update_action: 'consent_toggle',
      admin_id: input.adminId,
      is_enabled: input.enabled,
    },
  });
}

export function trackToggleTeamMfa(input: {
  teamId: string;
  enabled: boolean;
  adminId?: string;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Team,
    props: {
      action: SegmentActionName.TeamMfaToggle,
      teamId: input.teamId,
      adminId: input.adminId,
      enabled: input.enabled,
    },
  });

  // V2
  track({
    event_name: TrackingEventName.TeamUpdated,
    props: {
      team_id: input.teamId,
      update_action: 'mfa_toggle',
      admin_id: input.adminId,
      is_enabled: input.enabled,
    },
  });
}

export function trackUpdateTeamCustomConsentText(input: {
  teamId: string;
  adminId?: string;
  hasCustomText: boolean;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Team,
    props: {
      action: SegmentActionName.TeamCustomConsentTextUpdated,
      teamId: input.teamId,
      adminId: input.adminId,
      hasCustomText: input.hasCustomText,
    },
  });

  // V2
  track({
    event_name: TrackingEventName.TeamUpdated,
    props: {
      team_id: input.teamId,
      update_action: 'custom_consent_text_updated',
      admin_id: input.adminId,
      has_custom_text: input.hasCustomText,
    },
  });
}

export function trackUserJoinTeamSuccess(input: {
  teamId: string;
  invitedEmail: string;
  joinMethod?: string;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Team,
    props: {
      action: SegmentActionName.JoinTeamSuccess,
      teamId: input.teamId,
      ...(isOnPrem ? {} : { invitedEmail: input.invitedEmail }),
      joinTeamMethod: input.joinMethod,
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TeamMemberAccepted,
    props: {
      team_id: input.teamId,
      acceptance_action: 'join_team',
      // PII redaction (OBS-1.C4): invited_email stripped on on-prem —
      // the in-cluster collector must never receive raw email addresses.
      ...(isOnPrem ? {} : { invited_email: input.invitedEmail }),
      join_method: input.joinMethod,
    },
  });
}
